import { Observable } from 'rxjs';

/**
 * One transcript chunk emitted by a streaming provider.
 *  - `text` is the cumulative text for the current utterance.
 *  - `isFinal` is true only when the provider has finalised the utterance
 *    (no more revisions coming). The voice call card sends ONLY final
 *    chunks through `submitQuestion`; interim chunks drive the live
 *    "you're saying…" caption.
 */
export interface TranscriptionResult {
  text: string;
  isFinal: boolean;
  /** MEET-E-13-01: on a final, the provider's confidence averaged over the utterance's segments (0..1), when given. */
  confidence?: number;
}

/**
 * MEET-E-13-01: how a stream is started.
 *  - `meetId`: the Orbitax Meet this participant captions its own mic for; the token mint is then limited per
 *    meeting and per person (`/transcription-token?meetId=`).
 *  - `deviceId`: the microphone to open (the one the call uses); the browser default when absent.
 */
export interface TranscriptionStartOptions {
  meetId?: string;
  deviceId?: string;
}

/**
 * Shape returned by `POST /api/bot-agent/transcription-token`.
 * Provider-agnostic — `provider` tells the frontend which concrete
 * implementation should drive the streaming WebSocket.
 */
export interface TranscriptionTokenResponse {
  provider: 'Deepgram' | 'AssemblyAi' | string;
  token: string;
  expiresAtUtc: string;
  model: string;
  language: string;
  keyterms: string[];
  wsBaseUrl: string;
}

/**
 * Provider-agnostic transcription contract. The voice call card depends on
 * this abstract class (not any concrete service). Swapping providers later
 * is a single `useClass:` change in the module — no component edits.
 *
 * Lifecycle:
 *   start()  → opens mic + provider connection; transcripts flow on result$
 *   pause()  → freeze sending audio chunks (used while the bot speaks via
 *              TTS so we don't transcribe the bot's own voice as user input).
 *              Underlying connection stays open.
 *   resume() → un-freeze (counterpart to pause).
 *   stop()   → close everything; result$ stops emitting.
 *
 * Resilience: an implementation is expected to retry on its own if the underlying connection
 * drops unexpectedly (not from our own stop()) — the caller should never have to notice a
 * transient network blip. connectionLost$ fires only once automatic recovery has been exhausted,
 * so the host can tell the user capture genuinely needs their attention.
 */
export abstract class ITranscriptionService {
  abstract result$: Observable<TranscriptionResult>;
  /** Fires when the connection dropped unexpectedly and automatic reconnect attempts gave up, or the mic's audio stream ended. */
  abstract connectionLost$: Observable<void>;
  abstract isRunning: boolean;

  abstract start(options?: TranscriptionStartOptions): Promise<void>;
  abstract stop(): void;
  abstract pause(): void;
  abstract resume(): void;
}
